import React, { useState, useRef, useEffect } from 'react';

import styles from './EditableText.module.scss';

interface Props {
  text: string;
  onSubmit: (text: string) => void;
  testId?: string;
  labelId?: string;
  disabled?: boolean;
  onStateChange?: (type: 'focus' | 'blur' | 'enter') => void;
  afterContent?: React.ReactNode;
  hasInternalState?: boolean;
  placeholder?: string;
  isSearch?: boolean;
  isSearchLoading?: boolean;
  buttonRef?: React.RefObject<HTMLButtonElement>;
  buttonStyle?: string;
  hidePencil?: boolean;
  isPrice?: boolean;
}

const EditableText: React.FC<Props> = ({
  text: externalText,
  onSubmit,
  onStateChange,
  testId,
  labelId,
  disabled,
  afterContent,
  hasInternalState,
  placeholder = '',
  isSearch,
  isSearchLoading,
  buttonRef,
  buttonStyle,
  hidePencil = false,
  isPrice = false
}) => {
  const [internalText, setInternalText] = useState(externalText);

  const text = hasInternalState ? internalText : externalText;

  const [isEditing, setIsEditing] = useState(false);
  const [hasVisibleClass, setHasVisibleClass] = useState(false);

  const isInvalid = text.toLowerCase().includes('invalid');

  const inputRef = useRef<HTMLInputElement>(null);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement> | string): void => {
    const value = typeof e === 'string' ? e : e.target.value;

    if (hasInternalState) {
      setInternalText(value);
    } else {
      onSubmit(value);
    }
  };

  useEffect(() => {
    if (hasInternalState) {
      setInternalText(externalText);
    }
  }, [externalText, hasInternalState]);

  const handleBlur = (isEnter?: boolean): void => {
    // timeout here cause of odd race condition when clicking a button in the search results when used for search.
    setTimeout(
      () => {
        setHasVisibleClass(false);

        if (onStateChange) {
          onStateChange(isEnter ? 'enter' : 'blur');
        }

        if (hasInternalState) {
          onSubmit(internalText);
        }

        setTimeout(() => {
          setIsEditing(false);
        }, 100);
      },
      isSearch ? 200 : 0
    );
  };

  const handleFocus = (): void => {
    if (!disabled) {
      if (onStateChange) {
        onStateChange('focus');
      }

      if (isInvalid) {
        handleTextChange('0');
      }

      // it's a price range
      if (isPrice) {
        handleTextChange(text.split(' - ')[0]);
      }

      setIsEditing(true);

      window.requestAnimationFrame(() => {
        setHasVisibleClass(true);

        inputRef.current?.focus();
      });
    }
  };

  const inner = isEditing ? (
    <>
      {isSearch && <i className="mm-search" />}
      <input
        type="text"
        data-testid={testId}
        id={labelId}
        onChange={handleTextChange}
        onBlur={() => handleBlur()}
        className={`${styles.Input} ${hasVisibleClass ? styles.Visible : ''} amp-unmask`}
        placeholder={placeholder}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleBlur(true);
          }

          if (e.key === 'Escape') {
            handleBlur();
          }
        }}
        value={text}
        ref={inputRef}
      />
      {isSearch && (
        <button
          aria-label="clear search input"
          ref={buttonRef || undefined}
          type="button"
          onClick={() => handleBlur()}
          className={styles.Clear}
        >
          <i className={`mm-${isSearchLoading ? `circle-o-notch mm-spin` : `times`}`} />
        </button>
      )}
    </>
  ) : (
    <button data-testid={testId} type="button" onClick={handleFocus} ref={buttonRef || null} className={buttonStyle}>
      <span id={labelId} className={`${styles.Text} ${hidePencil ? '' : styles.hoverUnderline}`}>
        {text}
      </span>{' '}
      {!hidePencil ? <i className="mm-pencil" /> : null}
      {afterContent}
    </button>
  );

  return (
    <div
      className={`${styles.Outer} ${disabled ? styles.Disabled : ''} ${isInvalid ? styles.Invalid : ''} ${
        isEditing ? styles.Editing : ''
      } ${isSearch && styles.Search}`}
    >
      {inner}
    </div>
  );
};

export default EditableText;
